import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PremiumReceipt from "../Pages/settings/PremiumReceipt";
import {
  formatAmountPaise,
  formatMembershipDate,
  parseMembershipDate,
} from "../utils/membership";
import { paymentRecordToReceipt } from "../utils/paymentReceipt";

const AdminExtraRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-sand-100 py-2.5 last:border-0">
    <span className="text-[12.5px] text-ink-500">{label}</span>
    <span className="max-w-[60%] break-all text-right text-[12.5px] font-semibold text-ink-800">
      {value ?? "—"}
    </span>
  </div>
);

export default function PaymentDetailModal({
  payment,
  onClose,
  showAdminFields = false,
  footerAction,
}) {
  return (
    <AnimatePresence>
      {payment && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end justify-center bg-ink-900/45 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-[28px] bg-white shadow-elevated sm:rounded-3xl"
          >
            <div className="flex shrink-0 items-center justify-between border-b border-sand-200 bg-sand-50 px-5 py-4">
              <div>
                <h3 className="font-display text-lg font-bold text-ink-800">Payment receipt</h3>
                <p className="text-[12px] text-ink-400">Full transaction details</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="btn-icon h-10 w-10"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
              <PremiumReceipt receipt={paymentRecordToReceipt(payment)} />

              {showAdminFields && (
                <div className="mt-4 rounded-2xl border border-sand-200 bg-sand-50 px-4 py-3">
                  <p className="mb-2 text-[10.5px] font-bold uppercase tracking-wider text-brand-600">
                    Admin details
                  </p>
                  <AdminExtraRow label="Profile ID" value={payment.profileId} />
                  <AdminExtraRow label="User UID" value={payment.uid} />
                  <AdminExtraRow label="Phone" value={payment.memberPhone} />
                  <AdminExtraRow label="Email" value={payment.memberEmail} />
                  <AdminExtraRow label="Coupon code" value={payment.couponCode} />
                  <AdminExtraRow
                    label="Discount"
                    value={payment.discountAmount != null ? formatAmountPaise(payment.discountAmount, payment.currency) : "-"}
                  />
                  <AdminExtraRow
                    label="GST"
                    value={payment.gstAmount != null ? formatAmountPaise(payment.gstAmount, payment.currency) : "-"}
                  />
                  <AdminExtraRow
                    label="Savings"
                    value={payment.savingsAmount != null ? formatAmountPaise(payment.savingsAmount, payment.currency) : "-"}
                  />
                  <AdminExtraRow label="UPI VPA" value={payment.vpa} />
                  <AdminExtraRow label="Bank" value={payment.bank} />
                  <AdminExtraRow label="Wallet" value={payment.wallet} />
                  <AdminExtraRow
                    label="Card"
                    value={payment.cardLast4 ? `${payment.cardNetwork || ""} •••• ${payment.cardLast4}`.trim() : "—"}
                  />
                  <AdminExtraRow label="Razorpay status" value={payment.razorpayStatus} />
                  <AdminExtraRow
                    label="Fee"
                    value={payment.fee != null ? formatAmountPaise(payment.fee, payment.currency) : "—"}
                  />
                  <AdminExtraRow
                    label="Tax"
                    value={payment.tax != null ? formatAmountPaise(payment.tax, payment.currency) : "—"}
                  />
                  <AdminExtraRow label="Environment" value={payment.environment} />
                  <AdminExtraRow label="Transaction type" value={payment.transactionType} />
                </div>
              )}
            </div>

            {footerAction && (
              <div className="shrink-0 border-t border-sand-200 px-4 py-4 sm:px-5">
                {footerAction}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function PaymentHistoryItem({ payment, active, onClick }) {
  const receipt = paymentRecordToReceipt(payment);
  const paidLabel = formatMembershipDate(parseMembershipDate(payment.paidAt));
  const validUntil = formatMembershipDate(parseMembershipDate(payment.membershipExpiresAt));
  const amountLabel = formatAmountPaise(payment.amount, payment.currency);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start justify-between gap-3 rounded-2xl border px-4 py-3.5 text-left transition ${
        active
          ? "border-brand-600 bg-brand-50/40 shadow-soft"
          : "border-sand-200 bg-white hover:border-brand-300 hover:shadow-soft"
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-ink-800">
          {receipt.planName || "Premium"}
        </p>
        <p className="mt-0.5 text-[12px] text-ink-400">
          Paid {paidLabel}{validUntil !== "—" ? ` · Valid until ${validUntil}` : ""}
        </p>
        {payment.methodLabel && (
          <p className="mt-0.5 text-[11.5px] font-semibold text-brand-600">
            {payment.methodLabel}
            {payment.methodDetail ? ` · ${payment.methodDetail}` : ""}
          </p>
        )}
      </div>
      <div className="shrink-0 pt-0.5 text-right">
        <p className="font-display text-[15px] font-bold leading-tight text-brand-700">
          {amountLabel}
        </p>
        <p className="mt-1 text-[10.5px] font-bold uppercase tracking-wider capitalize text-emerald-600">
          {payment.status || "paid"}
        </p>
      </div>
    </button>
  );
}
